import { useEffect, useState, useRef } from 'react';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useDisableBodyScroll from '../../hooks/useDisableBodyScroll';
import Image from 'next/image';
import Link from 'next/link';
import styles from './Popup.module.scss';
import Button from '../Button/button';

export default function Popup({
  title,
  message,
  popupKey = 'seenPopup',
  delay = 0,
  duration = 24 * 60 * 60 * 1000, // Default: 24 hours
  expirationDate, 
  imageSrc,
  imageAlt,
  imageLink,
  buttonText,
  buttonLink,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  useDisableBodyScroll(isVisible);

  useEffect(() => {
    const now = Date.now();
  
    // Expiration check
    if (expirationDate) {
      const expiry = new Date(expirationDate).getTime();
      if (now > expiry) {
        localStorage.removeItem(popupKey); // 👈 Auto-clear the key
        return;
      }
    }
  
    const lastSeen = localStorage.getItem(popupKey);
    if (!lastSeen || now - Number(lastSeen) > duration) {
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [popupKey, delay, duration, expirationDate]);

  const handleClose = () => {
    localStorage.setItem(popupKey, Date.now().toString());
    setIsVisible(false);
  };

  useOnClickOutside(containerRef, handleClose);

  if (!isVisible) return null;

  return (
    <div className={styles.popup}>
      <div className={styles.container} ref={containerRef}>
        <button className={styles.closeButton} onClick={handleClose}>×</button>

        {imageSrc && (
          <div className={styles.imageWrapper}>
            {imageLink ? (
              <Link
                href={imageLink}
                aria-label={imageAlt || 'Popup image'}
                style={{ display: 'block', width: '100%', height: '100%' }}
              >
                <Image
                  src={imageSrc}
                  alt={imageAlt || ''}
                  fill
                  style={{ objectFit: 'contain' }}
                />
              </Link>
            ) : (
              <Image
                src={imageSrc}
                alt={imageAlt || ''}
                fill
                style={{ objectFit: 'contain' }}
              />
            )}
          </div>
        )}

        <div className={styles.content}>
          {title && <h2>{title}</h2>}
          {message && <p>{message}</p>}

          {buttonText && buttonLink && (
            <div className="flex center">
              <Button type="link" href={buttonLink} className="primary text-center">
                {buttonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
